/**
 * Created by Daniel Wang on 6/13/2024.
 * Description:
 * ------ maintenance history ------
 * 28/08/2024   Alex  added associated configuration
 */
import { Component, OnInit, OnDestroy, ChangeDetectorRef, HostListener } from '@angular/core';
import { Observable, Subscription, forkJoin, of } from 'rxjs';
import { LayoutService } from '../../services/layout.service';
import { businessConstants } from '../../constants/business.constants';
import { Store, select } from '@ngrx/store';
import { ScreenActionTypes } from '../../redux/actions/screen.actions';
import { ActivatedRoute, Router } from '@angular/router';
import { StringLiteralsPipe } from '../../pipes/translate.pipe';
import { AlertBtnTypes } from '../../widgets/alert-window/alert-window.model';
import { catchError, map, switchMap, take } from 'rxjs/operators';
import { DELETE_ENTITY, DELETE_ENTITY_SUCCESS } from '../../tamalelibs/redux/entity-list.actions';
import { AlertWindowService } from '../../widgets/alert-window/alert-window.service';
import { lastCustomActionSelector } from '../../redux/reducers/action-history.reducer';
import { ToastService } from '../../widgets/toast/toast.service';
import { NotificationStyles } from '../../widgets/notification/notification.model';
import { ColDef, GridOptions } from 'ag-grid-community';
import { EntryService } from '../../tamalelibs/services/entry.service';
import { EntityService } from '../../tamalelibs/services/entity.service';
import { RelationshipService } from '../../tamalelibs/services/relationship.service';
import { EntityAssociatedType, SourceType } from '../entity/entity.model';
import { ContextMenuConfig, ContextMenuShowAction } from '../../widgets/tam-context-menu/tam-context-menu.model';
import { DatePipe } from '@angular/common';
import { UtilsService } from '../../tamalelibs/services/utils.service';
import { DateHelperWebService } from '../../tamalelibs/services/date-helper.web.service';
import { DELETE_CONTACT, DELETE_CONTACT_SUCCESS } from '../../tamalelibs/redux/contact-list.actions';
import * as _lodash from 'lodash';
import { Entity } from '../../tamalelibs/models/entity.model';
import { ContactService } from '../../tamalelibs/services/contact.service';
import { TabItem, TabOptions } from '../../widgets/tabs/tabs.model';
import { ImpactedDashboardConfig } from '../../tamalelibs/models/impacted-dashboard.model';
import { EmailDigestType } from '../../tamalelibs/models/emaildigest.model';
import { ArrayHelperService } from '../../tamalelibs/services/array-helper.service';

@Component({
    selector: 'tam-entity-associated-info',
    templateUrl: './entity-associated-info.component.html',
    styleUrls: ['./entity-associated-info.component.scss']
})
export class EntityAssociatedInfoComponent implements OnInit, OnDestroy {
    // #region public properties
    config: Array<any> = [];
    configImpacted: Array<any> = [];
    configData: Array<any> = [];
    configImpactedData: Array<any> = [];
    configEmptyData: Array<any> = [];
    configImpactEmptyData: Array<any> = [];
    columnDefs: Array<ColDef>;
    contextMenuConfig: ContextMenuConfig;
    currentTab = 0;
    entityInfo: any;
    gridOptions: GridOptions;
    isPageLoading: boolean;
    isExportToExcel = false;
    pageTitle = '';
    source = SourceType.ENTITY;
    tabsOptions: TabOptions;
    // #endregion

    // #region private properties
    private _columnState;
    private _gridApiMap = new Map();
    private _gridColumnApiMap = new Map();
    private _filterData;
    private _keyword = '';
    private _subscriptions: Array<Subscription> = [];
    //#endregion

    //#region constructor
    constructor(
        private _alert: AlertWindowService,
        private _changeDetectorRef: ChangeDetectorRef,
        private _contactService: ContactService,
        private _datepipe: DatePipe,
        private _entityService: EntityService,
        private _entryService: EntryService,
        private _layoutService: LayoutService,
        private _relationshipService: RelationshipService,
        private _router: Router,
        private _route: ActivatedRoute,
        private _store: Store<any>,
        private _toast: ToastService,
        private _utilsService: UtilsService
    ) {
        // get entity information from router
        this.entityInfo = this._router.getCurrentNavigation()?.extras?.state;
    }
    //#endregion

    //#region Angular Events
    //#region ngOnInit
    ngOnInit() {
        this.isPageLoading = true;
        this._route.queryParams.pipe(take(1)).subscribe(params => {
            this.source = params.source;
            this._keyword = params.keyword;
        });

        if (this.entityInfo) {
            this._initPage();
        } else {
            this.entityInfo = {};
            const entityId = this._route.snapshot.url[1].path;
            this._entityService.getEntityDetailWithoutAdhoc(entityId).pipe(take(1)).subscribe(res => {
                const entity = Entity.parse(res);
                this.entityInfo['id'] = entity.id;
                this.entityInfo['shortName'] = entity.shortName;
                this.entityInfo['name'] = entity.name;
                this.entityInfo['isPublic'] = entity.isPublic;
                this._initPage();
            });
        }
    }
    //#endregion

    //#region ngOnDestroy
    ngOnDestroy() {
        this._subscriptions.forEach(subscription => {
            if (subscription && !subscription.closed) {
                subscription.unsubscribe();
            }
        });
        // set the top of sub-chrome back to 119px (the default value).
        this._layoutService.changeSubTitleTopOpen$.next('119px');
    }
    //#endregion
    //#endregion

    //#region public functions
    /**
     * export the grid to excel
     * @param data
     */
    exportToExcel(data) {
        this._exportToExcel(data);
    }

    /**
     * Delete current entity
     */
    onDelete() {
        if (this.source === SourceType.ENTITY) {
            const deleteEntityTitle = StringLiteralsPipe.translate('crm.delete_entities');
            const deleteEntityTip = StringLiteralsPipe.translate('crm.delete_entities_tip', this.entityInfo.name);

            this._alert.warn(deleteEntityTitle, [deleteEntityTip], 'Delete', 'Cancel', '', '', AlertBtnTypes.destructive).pipe(
                take(1),
            ).subscribe(result => {
                if (result) {
                    this._store.dispatch({
                        type: DELETE_ENTITY,
                        payload: this.entityInfo.id
                    });
                }
            });
        } else if (this.source === SourceType.CONTACT) {
            const deleteContactTitle = StringLiteralsPipe.translate('crm.delete_contacts');
            const deleteContactTip = StringLiteralsPipe.translate('crm.delete_contacts_tip', this.entityInfo.name);

            this._alert.warn(deleteContactTitle, [deleteContactTip], 'Delete', 'Cancel', '', '', AlertBtnTypes.destructive).pipe(
                take(1),
            ).subscribe(result => {
                if (result) {
                    this._store.dispatch({
                        type: DELETE_CONTACT,
                        payload: this.entityInfo.id
                    });
                }
            });
        }
    }

    /**
     * Back to previous page
     */
    onBackClick() {
        if (this.source === SourceType.ENTITY) {
            this._router.navigate(['entities'], { state: { isShowBulkAction: true, keyword: this._keyword } });
        } else if (this.source === SourceType.CONTACT) {
            this._router.navigate(['contacts'], { state: { isShowBulkAction: true, keyword: this._keyword } });
        }
    }

    /**
     * ag-grid ready
     * @param params
     * @param id
     */
    onGridReady(params, id: EntityAssociatedType) {
        this._gridApiMap[id] = params.api;
        this._gridColumnApiMap[id] = params.columnApi;
        // set filter and sort when refreshing the grid
        if (this._filterData) {
            this._gridApiMap[id].setFilterModel(this._filterData);
            this._gridApiMap[id].onFilterChanged();
        }
        if (this._columnState) {
            this._gridColumnApiMap[id].applyColumnState({ state: this._columnState });
        }
        // set filter and sort to null after refreshing.
        this._filterData = null;
        this._columnState = null;
        this._gridApiMap[id].sizeColumnsToFit();
    }

    @HostListener('window:resize', ['$event'])
    onResize(e) {
        Object.keys(EntityAssociatedType).forEach(key => {
            if (this._gridApiMap[key]) {
                this._gridApiMap[key].sizeColumnsToFit();
            }
        });
    }

    /**
     * refresh grid manually
     * @param event
     * @param type
     */
    refreshGrid(event, item) {
        item.isDataLoading = true;
        this.isExportToExcel = false;
        const gridApi = this._gridApiMap[item.id];
        const columnApi = this._gridColumnApiMap[item.id];
        this._filterData = gridApi?.getFilterModel();
        this._columnState = columnApi?.getColumnState();
        if (item.id === EntityAssociatedType.ENTRY) {
            this._entryService.getEntryListByEntityId(this.entityInfo.id).pipe(take(1)).subscribe(res => {
                this.config[0].rowData = res;
                this.config[0].displayDataCount = res.length;

                item.isDataLoading = false;
            });
        } else if (item.id === EntityAssociatedType.RELATIONSHIP) {
            this._getRelationshipRelatedInfo().pipe(take(1)).subscribe(res => {
                const data = res[0].concat(...res[1]);
                this.config[1].rowData = data;
                this.config[1].displayDataCount = data.length;

                item.isDataLoading = false;
            });
        } else if (item.id === EntityAssociatedType.ENTITY) {
            this._entityService.getEntityInTopicMemberByEntityId(this.entityInfo.id).pipe(take(1)).subscribe(res => {
                this.config[2].rowData = res;
                this.config[2].displayDataCount = res.length;

                item.isDataLoading = false;
            });
        } else if (item.id === EntityAssociatedType.TEAM) {
            this._entryService.getTeamsByEntityId(this.entityInfo.id).pipe(take(1)).subscribe(res => {
                this.config[3].rowData = res;
                this.config[3].displayDataCount = res.length;

                item.isDataLoading = false;
            });
        } else if (item.id === EntityAssociatedType.CONFIG) {
            this._initImpactedConfigData();
        }
    }

    /**
     * show context menu
     * @param e
     * @param data
     */
    showContextMenu(e: MouseEvent, data) {
        this.contextMenuConfig.show$.next(new ContextMenuShowAction(e, data));
    }
    //#endregion

    //#region private functions
    /**
     * receive action message after the entity was deleted.
     * @param action
     */
    private _actionMessageHandler(action) {
        let messageInfo = '';
        if (action.type === DELETE_ENTITY_SUCCESS) {
            messageInfo = action.payload.name + ' is deleted successfully.';
            this._toast.notify({
                style: NotificationStyles.Success,
                message: messageInfo
            });
            this._router.navigate(['entities'], { state: { isShowBulkAction: true, keyword: this.entityInfo.keyword } });
        } else if (action.type === DELETE_CONTACT_SUCCESS) {
            messageInfo = action.payload.name + ' is deleted successfully.';

            this._toast.notify({
                style: NotificationStyles.Success,
                message: messageInfo
            });
            this._router.navigate(['contacts'], { state: { isShowBulkAction: true, keyword: this.entityInfo.keyword } });
        }
    }

    /**
     * export grid to excel
     * @param data
     */
    private _exportToExcel(data) {
        // on the tab of Impacted configuration
        if (data.id === 'CONFIG') {
            this.isExportToExcel = true;
        }
        // get current date
        const currentDate = this._datepipe.transform(new Date(), 'yyyyMMddHHmm');
        // generate the excel name by short name, grid name and current date
        const excelName = this.entityInfo.shortName + '_' + data.name + '_' + currentDate + '.xlsx';
        // export the grid to excel
        this._utilsService.exportToXLS(this._gridColumnApiMap[data.id], this._initAGGridColumns(data.id), this._gridApiMap[data.id], 'Sheet1', excelName);
    }

    /**
     * export grid to separate excel by entry type
     * @param data
     */
    private _exportToExcelByEntryType(data) {
        let entryTypes = [];
        const gridApi = this._gridApiMap[data.id];
        const currentDate = this._datepipe.transform(new Date(), 'yyyyMMddHHmm');
        // get the filter which is setted by user manually
        const filtersData = gridApi.getFilterModel();
        const originalFiltersData = _lodash.cloneDeep(filtersData);

        // get all entry types
        gridApi.forEachNode(function (node) {
            if (!entryTypes.includes(node.data.entryType)) {
                entryTypes.push(node.data.entryType);
            }
        });
        if (filtersData['entryType']) {
            entryTypes = filtersData['entryType'].values;
        }

        // export each excel by entry type
        entryTypes.forEach(item => {
            // set the filter by entry type
            const filter = {
                entryType: {
                    type: 'set',
                    values: [item],
                },
            };
            // remove entry type from filtersDate if it exists.
            if (filtersData['entryType']) {
                delete filtersData['entryType'];
            }
            // combine the filters
            const combineFilter = { ...filtersData, ...filter };

            // generate excel name
            const excelName = this.entityInfo.shortName + '_' + item + '_' + currentDate + '.xlsx';
            // set filter for the entry type
            gridApi.setFilterModel(combineFilter);
            gridApi.onFilterChanged();
            if (gridApi.rowModel.rowsToDisplay.length > 0) {
                // export the excel by current entry type filter
                this._utilsService.exportToXLS(this._gridColumnApiMap[data.id], this._initAGGridColumns(data.id), this._gridApiMap[data.id], 'Sheet1', excelName);
            }
        });
        // reset the fitler to null
        gridApi.setFilterModel(originalFiltersData);
        gridApi.onFilterChanged();
    }

    /**
     * get the related information by current entity id.
     * @returns
     */
    private _getAssociatedInfo(): Observable<any> {
        const observable = new Observable(subscriber => {
            subscriber.next();
            subscriber.complete();
        });

        return observable.pipe(
            switchMap(() => {
                const observables = [];
                observables.push(this._entryService.getEntryListByEntityId(this.entityInfo.id));
                observables.push(this._relationshipService.getRelationshipListByEntityId(this.entityInfo.id));
                if (this.source === SourceType.USER) {
                    observables.push(this._entityService.getEntityInTopicMemberByEntityId(this.entityInfo.id));
                    observables.push(this._entryService.getTeamsByEntityId(this.entityInfo.id));
                }
                observables.push(this._contactService.getEmploymentListByEntityId(this.entityInfo.id));
                return forkJoin(observables).pipe(
                    take(1),
                    map((data) => {
                        return data;
                    })
                );
            })
        );
    }

    /**
     * get impacted configuration information
     * @returns
     */
    private _getImpactedInfo(): Observable<any> {
        const observable = new Observable(subscriber => {
            subscriber.next();
            subscriber.complete();
        });

        return observable.pipe(
            switchMap(() => {
                const observables = [];
                observables.push(this._entityService.getImpactedEmailDigestsByEntityId(this.entityInfo.id));
                observables.push(this._entityService.getImpactedDashboardsByEntityId(this.entityInfo.id));
                return forkJoin(observables).pipe(
                    take(1),
                    map((data) => {
                        return data;
                    })
                );
            })
        );
    }

    /**
     * get relationship related information
     * @returns
     */
    private _getRelationshipRelatedInfo(): Observable<any> {
        const observable = new Observable(subscriber => {
            subscriber.next();
            subscriber.complete();
        });

        return observable.pipe(
            switchMap(() => {
                const observables = [];
                observables.push(this._relationshipService.getRelationshipListByEntityId(this.entityInfo.id));
                observables.push(this._contactService.getEmploymentListByEntityId(this.entityInfo.id));
                return forkJoin(observables).pipe(
                    take(1),
                    map((data) => {
                        return data;
                    })
                );
            })
        );
    }

    /**
     * init ag-grid columns
     * @param type
     * @returns
     */
    private _initAGGridColumns(type: EntityAssociatedType) {
        if (type === EntityAssociatedType.ENTRY) {
            this.columnDefs = [{
                headerName: 'Entry ID',
                field: 'id',
                colId: 'id',
                width: 280,
            }, {
                headerName: 'Entry Type',
                field: 'entryType',
                colId: 'entryType',
            }, {
                headerName: 'Field',
                field: 'field',
                colId: 'field',
            }, {
                headerName: 'Submitted Date',
                field: 'submittedDate',
                colId: 'submittedDate',
                menuTabs: [],
                cellRenderer: (data) => {
                    return data.value ? DateHelperWebService.getWeekdayByDate(data.value) + ' ' +
                        this._datepipe.transform(data.value, 'MM/dd/yyyy HH:mm') : '';
                },
            }, {
                headerName: 'Display Date',
                field: 'displayDate',
                colId: 'displayDate',
                menuTabs: [],
                cellRenderer: (data) => {
                    return data.value ? DateHelperWebService.getWeekdayByDate(data.value) + ' ' +
                        this._datepipe.transform(data.value, 'MM/dd/yyyy HH:mm') : '';
                },
            }];
        } else if (type === EntityAssociatedType.RELATIONSHIP) {
            this.columnDefs = [{
                headerName: 'Relationship Type ID',
                field: 'relationshipType.id',
                colId: 'relationshipTypeID',
                menuTabs: [],
            }, {
                headerName: 'Relationship Type',
                field: 'relationshipType.name',
                colId: 'relationshipType',
            }, {
                headerName: 'Parent Entity ID',
                field: 'parentEntity.id',
                colId: 'parentEntityID',
                menuTabs: [],
            }, {
                headerName: 'Parent Entity Short Name',
                field: 'parentEntity.shortName',
                colId: 'parentEntityShortName',
            }, {
                headerName: 'Parent Entity Name',
                field: 'parentEntity.name',
                colId: 'parentEntityName',
            }, {
                headerName: 'Child Entity ID',
                field: 'childEntity.id',
                colId: 'childEntityID',
                menuTabs: [],
            }, {
                headerName: 'Child Entity Short Name',
                field: 'childEntity.shortName',
                colId: 'childEntityShortname',
            }, {
                headerName: 'Child Entity Name',
                field: 'childEntity.name',
                colId: 'childEntityName',
            }];
        } else if (type === EntityAssociatedType.ENTITY) {
            this.columnDefs = [{
                headerName: 'Entity ID',
                field: 'entityId',
                colId: 'entityId',
                menuTabs: [],
            }, {
                headerName: 'Short Name',
                field: 'shortName',
                colId: 'shortName',
            }, {
                headerName: 'Long Name',
                field: 'longName',
                colId: 'longName',
            }, {
                headerName: 'Is Public',
                field: 'isPublic',
                colId: 'isPublic',
            }];
        } else if (type === EntityAssociatedType.TEAM) {
            this.columnDefs = [{
                headerName: 'Entity ID',
                field: 'entityId',
                colId: 'entityId',
                menuTabs: [],
            }, {
                headerName: 'Short Name',
                field: 'shortName',
                colId: 'shortName'
            }, {
                headerName: 'Long Name',
                field: 'longName',
                colId: 'longName',
            }, {
                headerName: 'Is Public',
                field: 'isPublic',
                colId: 'isPublic',
            }];
        } else if (type === EntityAssociatedType.CONFIG) {
            this.columnDefs = [{
                headerName: 'Configuration name',
                field: 'name',
                colId: 'name',
                sort: 'asc',
                comparator: ArrayHelperService.caseInsensitiveComparator,
                width: 40,
                tooltipField: 'name',
                menuTabs: [],
            }, {
                headerName: 'Type',
                field: 'type',
                colId: 'type',
                width: 20,
            }, {
                headerName: 'Setting',
                field: 'setting',
                colId: 'setting',
                tooltipField: 'setting',
                cellRenderer: (params) => {
                    if (this.isExportToExcel) {
                        return params.value;
                    } else {
                        const element = document.createElement('span');
                        element.className = 'ellipsis-cell';
                        element.innerHTML = ImpactedDashboardConfig.highlightText(params.value);
                        return element;
                    }
                }
            }];
        }
        return this.columnDefs;
    }

    /**
     * init ag-grid configuration columns and data
     * @param data
     */
    // all the source types have entries and relationships
    private _initAGGridConfigData(data) {
        this.config = [];
        this.config.push({
            id: EntityAssociatedType.ENTRY,
            name: 'Associated entries',
            columnDefs: this._initAGGridColumns(EntityAssociatedType.ENTRY),
            rowData: this._initAGGridRows(data, EntityAssociatedType.ENTRY),
            displayDataCount: data[0].length,
            isDataLoading: false,
            onFilterChanged: () => this._onFilterChanged(data, EntityAssociatedType.ENTRY)
        }, {
            id: EntityAssociatedType.RELATIONSHIP,
            name: 'Relationships with other entities',
            columnDefs: this._initAGGridColumns(EntityAssociatedType.RELATIONSHIP),
            rowData: this._initAGGridRows(data, EntityAssociatedType.RELATIONSHIP),
            displayDataCount: data[1].length,
            isDataLoading: false,
            onFilterChanged: () => this._onFilterChanged(data, EntityAssociatedType.RELATIONSHIP)
        });
        // only user has private entity and teams
        if (this.source === SourceType.USER) {
            this.config.push({
                id: EntityAssociatedType.ENTITY,
                name: 'In private entity access list',
                columnDefs: this._initAGGridColumns(EntityAssociatedType.ENTITY),
                rowData: this._initAGGridRows(data, EntityAssociatedType.ENTITY),
                displayDataCount: data[2].length,
                isDataLoading: false,
                onFilterChanged: () => this._onFilterChanged(data, EntityAssociatedType.ENTITY)
            }, {
                id: EntityAssociatedType.TEAM,
                name: 'In teams',
                columnDefs: this._initAGGridColumns(EntityAssociatedType.TEAM),
                rowData: this._initAGGridRows(data, EntityAssociatedType.TEAM),
                displayDataCount: data[3].length,
                isDataLoading: false,
                onFilterChanged: () => this._onFilterChanged(data, EntityAssociatedType.TEAM)
            });
        }
        this.config.forEach(item => {
            if (item.rowData?.length > 0) {
                this.configData.push(item);
            } else {
                this.configEmptyData.push(item);
            }
        });
    }

    /**
    * init ag-grid configuration columns and data
    * @param data
    */
    private _initAGGridImpactedData(data) {
        this.configImpacted = [];
        this.configImpactedData = [];
        this.configImpactEmptyData = [];
        this.configImpacted.push({
            id: EntityAssociatedType.CONFIG,
            name: businessConstants.impactedConfigTabs.configurations,
            columnDefs: this._initAGGridColumns(EntityAssociatedType.CONFIG),
            rowData: this._initAGGridRows(data, EntityAssociatedType.CONFIG),
            displayDataCount: data[0].length,
            isDataLoading: false,
            onFilterChanged: () => this._onFilterChanged(data, EntityAssociatedType.CONFIG)
        });

        this.configImpacted.forEach(item => {
            if (item.rowData?.length > 0) {
                this.configImpactedData.push(item);
            } else {
                this.configImpactEmptyData.push(item);
            }
        });
    }

    /**
     * init ag-grid data
     */
    private _initAGGridData() {
        this._getAssociatedInfo().pipe(
            take(1),
            catchError(error => {
                return of(error);
            })
        ).subscribe(res => {
            if (res) {
                // combone relationship and employment list
                if (this.source === SourceType.USER) {
                    res[1] = res[1].concat(...res[4]);
                } else {
                    res[1] = res[1].concat(...res[2]);
                }
                this._initAGGridConfigData(res);
                this.isPageLoading = false;
            }
        });
    }

    /**
     * init impacted configurations
     */
    private _initImpactedConfigData() {
        this._getImpactedInfo().pipe(
            take(1),
            catchError(error => {
                return of(error);
            })
        ).subscribe(res => {
            if (res && res[0]) {
                res[0] = res[0].concat(...res[1]);
                this._initAGGridImpactedData(res);
                this.isPageLoading = false;
            }
        });
    }

    /**
     * init ag-grid rows
     * @param data
     * @param type
     * @returns
     */
    private _initAGGridRows(data, type: EntityAssociatedType) {
        if (type === EntityAssociatedType.ENTRY || type === EntityAssociatedType.CONFIG) {
            return data[0];
        } else if (type === EntityAssociatedType.RELATIONSHIP) {
            return data[1];
        } else if (type === EntityAssociatedType.ENTITY) {
            return data[2];
        } else if (type === EntityAssociatedType.TEAM) {
            return data[3];
        }
    }

    /**
     * init ag-grid properties
     */
    private _initAGGridProperties() {
        this.gridOptions = {
            defaultColDef: {
                sortable: true,
                resizable: true,
                filter: 'agSetColumnFilter',
                menuTabs: ['filterMenuTab'],
            },
            cacheBlockSize: 50,
            suppressCellSelection: true,
            enableBrowserTooltips: true,
            rowSelection: 'single',
            rowGroupPanelShow: 'always',
            sortingOrder: ['desc', 'asc'],
            suppressContextMenu: true,
            suppressDragLeaveHidesColumns: true,
            overlayNoRowsTemplate: '<span style=\'font-size: 20px;background-color:white;color:black\'>No Data Found</span>'
        };
    }

    /**
     * init context menu
     */
    private _initContextMenu() {
        this.contextMenuConfig = new ContextMenuConfig();
        this.contextMenuConfig.stateful = false;
        this.contextMenuConfig.showItemIcon = false;
        this.contextMenuConfig.clickOnDiv = true;
        this.contextMenuConfig.items = [];
        this.contextMenuConfig.items.push(
            { id: '0', text: StringLiteralsPipe.translate('general.export_to_excel') },
            { id: '1', text: StringLiteralsPipe.translate('general.export_to_separate_excel_per_entry_type') },
        );
        this.contextMenuConfig.events$.subscribe(event => this._onMenuSelect(event));
    }

    private _initPage() {
        if (!this._changeDetectorRef['destroyed']) {
            // set the top of the sub-chrome, neen't sub-chrome in entity associated info
            this._layoutService.changeSubTitleTopOpen$.next('64px');
        }
        this.isExportToExcel = false;
        this._initContextMenu();
        this._initPageInfo();
        this._initAGGridProperties();
        this._initAGGridData();
        this._initImpactedConfigData();
        this._initTabs();

        this._subscriptions.push(
            this._store.pipe(
                select(lastCustomActionSelector),
            ).subscribe(action => this._actionMessageHandler(action))
        );
    }

    /**
     * init component inforation.
     */
    private _initPageInfo() {
        // remove page title
        let currentPageInfo: any = {
            url: this._route.snapshot.url,
            name: '',
            type: 'ENTITIES',
            screenType: businessConstants.screenType.entity
        };
        if (this._route.snapshot.url && this._route.snapshot.url[0].path === 'contact') {
            // set selected style for contact item on navigation menu
            currentPageInfo = {
                url: this._route.snapshot.url,
                name: '',
                type: 'CONTACTS',
                screenType: businessConstants.screenType.contact
            };
        }
        this._store.dispatch({
            type: ScreenActionTypes.SET_VALUE,
            payload: currentPageInfo
        });
        // update page title
        if (this.entityInfo.shortName === this.entityInfo.name) {
            this.pageTitle = this.entityInfo.shortName + ', ' + this.source + ' ID: ' + this.entityInfo.id;
        } else {
            this.pageTitle = this.entityInfo.shortName + ' (' + this.entityInfo.name + '), ' + this.source + ' ID: ' + this.entityInfo.id;
        }
    }

    private _initTabs() {
        const menuItems = [
            new TabItem(businessConstants.impactedConfigTabs.associated),
            new TabItem(businessConstants.impactedConfigTabs.configurations),
        ];

        this.tabsOptions = new TabOptions(menuItems, 0, false, { 'font-size': '14px', 'height': '30px' });

        this._subscriptions.push(
            this.tabsOptions.events$.subscribe(event => {
                if (event.type === 'TAB_CHANGED') {
                    this.currentTab = event.payload;
                    // set the value of isExportToExcel to default
                    this.isExportToExcel = false;
                }
            }),
        );
    }

    /**
     * update item number if filter is changed
     * @param type
     */
    private _onFilterChanged(data, type: EntityAssociatedType) {
        const gridApi = this._gridApiMap[type];
        this.config.forEach(item => {
            if (item.id === type) {
                item.displayDataCount = gridApi.rowModel.rowsToDisplay.length;
            }
        });
    }

    /**
     * menu events
     * @param event
     */
    private _onMenuSelect(event) {
        if (event.payload) {
            const id = event.payload.id;
            const data = event.payload.data;
            switch (id) {
                case '0':
                    this._exportToExcel(data);
                    break;
                case '1':
                    this._exportToExcelByEntryType(data);
                    break;
                default:
                    break;
            }
        }
    }
    //#endregion
}
